/**
 * Read-only test mode (2026-09-04) — REACT_APP_READ_ONLY=true.
 *
 * Lets a local build browse the LIVE upload library while refusing every write: any non-GET call to
 * /api/** is answered inside the browser with a 403 and never reaches the server. /auth/** stays open
 * so sign-in, sign-out and the CSRF handshake still work. Installed once from index.js, like the
 * App Check fetch wrapper.
 */
const READ_ONLY = String(process.env.REACT_APP_READ_ONLY || "").toLowerCase() === "true";
/** Local dev builds install the guard too, dormant, so "Test data" mode can switch it on at runtime. */
const DEV_BUILD = process.env.NODE_ENV === "development";
let runtimeReadOnly = false;

/** Test data mode (2026-09-15): while the app runs on the synthetic set, every /api write is refused
 *  in the browser so nothing fake can reach the live database. Flip with the toggle, not the env. */
export function setRuntimeReadOnly(on) {
  runtimeReadOnly = !!on;
}
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export const READ_ONLY_MESSAGE =
  "Read-only test mode: this local build reads the live data but cannot change it. " +
  "Set REACT_APP_READ_ONLY=false in jf-billing/.env.development to allow writes.";

export function isReadOnlyTestMode() {
  return READ_ONLY || runtimeReadOnly;
}

function isApiWrite(input, init) {
  try {
    const method = String(
      (init && init.method) || (input && typeof input !== "string" && input.method) || "GET",
    ).toUpperCase();
    if (SAFE_METHODS.has(method)) return false;
    const raw = typeof input === "string" ? input : (input && input.url) || "";
    const url = new URL(raw, window.location.origin);
    const apiOrigin = API_BASE ? new URL(API_BASE, window.location.origin).origin : window.location.origin;
    return url.origin === apiOrigin && url.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

export function installReadOnlyFetchGuard() {
  if ((!READ_ONLY && !DEV_BUILD) || typeof window === "undefined" || window.__jfReadOnlyGuardInstalled) return;
  window.__jfReadOnlyGuardInstalled = true;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    if (!(READ_ONLY || runtimeReadOnly) || !isApiWrite(input, init)) return nativeFetch(input, init);
    const path = typeof input === "string" ? input : (input && input.url) || "";
    // eslint-disable-next-line no-console
    console.warn("[read-only] blocked write:", path);
    return new Response(JSON.stringify({ error: READ_ONLY_MESSAGE, readOnly: true }), {
      status: 403,
      headers: { "Content-Type": "application/json" },
    });
  };
  // eslint-disable-next-line no-console
  if (READ_ONLY) console.warn("[jf-billing] " + READ_ONLY_MESSAGE);
}
